/**
 * One attempt at one cave (docs/spec/02 §8, 04 §4): the cover dissolves while the cave comes alive,
 * Digby plays it, and it ends in the time-bonus countdown, a death or OUT OF TIME, then the cover
 * returns. Scans run on a fixed tick; everything between ticks is presentation.
 */

import type { BuiltCave } from '../../caves/types';
import type { Options } from '../../platform/options';
import { Camera } from '../../render/camera';
import { digbyPixel } from '../../render/caveView';
import { Cover } from '../../render/cover';
import { Particles } from '../../render/effects/particles';
import { createCave, step } from '../../sim';
import type { CaveState, Input, SimEvent } from '../../sim';
import type { RunAudio } from './runAudio';

export type RunPhase = 'play' | 'bonus' | 'dead' | 'timeout' | 'outro' | 'done';

export interface RunHooks {
  readonly audio: RunAudio;
  options(): Options;
  /** Points scored: returns how many bonus lives they earned. */
  score(points: number): number;
  /** The input for the next scan. */
  input(s: CaveState): Input;
  /** Tell screen readers (the exit opening, time running out, a death). */
  announce(text: string): void;
}

const BONUS_STEP_MS = 16;
const DEATH_WAIT_MS = 2500;
const TIMEOUT_WAIT_MS = 4000;

export class CaveRun {
  readonly state: CaveState;
  readonly cover = new Cover();
  readonly camera = new Camera();
  readonly particles = new Particles();
  phase: RunPhase = 'play';
  /** Real milliseconds since the run began: the animation clock. */
  clock = 0;
  /** 0..1 through the current tick. */
  t = 0;
  flashMs = 0;
  sparkleMs = 0;
  /** Seconds still to be counted into the score at the exit. */
  bonusLeft = 0;
  private acc = 0;
  private phaseMs = 0;
  private lastScore = 0;
  private bonusTicks = 0;

  constructor(readonly built: BuiltCave, seed: number, private readonly hooks: RunHooks) {
    this.state = createCave(built, seed);
    this.cover.start('uncover', built.w, built.h, seed);
    this.hooks.audio.cue('start');
  }

  /** Still dissolving in: the status line shows who is playing rather than the numbers. */
  get introducing(): boolean {
    return this.cover.mode === 'uncover' && !this.cover.done;
  }

  get anim(): number {
    return Math.floor(this.clock / 40);
  }

  /** Real milliseconds per scan (Relaxed speed stretches every tick). */
  private tickMs(): number {
    return this.state.params.tickMs * (this.hooks.options().speed === 'relaxed' ? 1.25 : 1);
  }

  update(dtMs: number): void {
    const dt = Math.min(dtMs, 100);
    this.clock += dt;
    this.phaseMs += dt;
    this.cover.update(dt);
    if (this.cover.mode === 'uncover' && this.cover.done) this.cover.clear();
    this.particles.update(dt);
    this.flashMs = Math.max(0, this.flashMs - dt);
    this.sparkleMs = Math.max(0, this.sparkleMs - dt);

    if (this.phase === 'play' || this.phase === 'dead') this.runScans(dt);
    if (this.phase === 'bonus') this.countBonus(dt);
    if (this.phase === 'dead' && this.phaseMs >= DEATH_WAIT_MS) this.startOutro();
    if (this.phase === 'timeout' && this.phaseMs >= TIMEOUT_WAIT_MS) this.startOutro();
    if (this.phase === 'outro' && this.cover.done && this.phaseMs > Cover.durationMs(this.built.w) + 250) this.phase = 'done';

    const s = this.state;
    const opts = this.hooks.options();
    const px = digbyPixel(s, this.t, opts.smooth);
    this.camera.follow(px.x, px.y, this.viewW, this.viewH, s.w, s.h, this.tickMs(), dt);
    this.hooks.audio.ambience({
      amoeba: s.amoeba.phase === 'awake' && this.phase === 'play',
      magic: s.magic.phase === 'active' && this.phase === 'play',
      cover: this.cover.active && !this.cover.done,
    });
  }

  /** The view in pixels, set by the scene from the screen size before each update. */
  viewW = 320;
  viewH = 192;

  /** Centre the view on the inbox at the start. */
  snapCamera(): void {
    const s = this.state;
    this.camera.snap(s.digby.x * 16, s.digby.y * 16, this.viewW, this.viewH, s.w, s.h);
  }

  private runScans(dt: number): void {
    const tick = this.tickMs();
    this.acc += dt;
    while (this.acc >= tick) {
      this.acc -= tick;
      const s = this.state;
      step(s, this.hooks.input(s));
      this.react(s.events);
      if (s.status === 'won' || s.status === 'timeout') {
        this.acc = 0;
        break;
      }
    }
    this.t = Math.min(1, this.acc / tick);
    const s = this.state;
    if (this.phase === 'play' && s.status === 'dead') this.enter('dead');
    else if (s.status === 'won') {
      this.bonusLeft = s.timeLeft;
      this.enter('bonus');
    } else if (s.status === 'timeout') this.enter('timeout');
  }

  private react(events: readonly SimEvent[]): void {
    const s = this.state;
    const opts = this.hooks.options();
    this.hooks.audio.events(events, (this.camera.x + this.viewW / 2) / 16);
    if (opts.particles) this.particles.events(events, this.built.colors, opts.shake);
    for (const e of events) {
      if (e.type === 'gateOpen') {
        if (!opts.reduceFlashing) this.flashMs = 60;
        this.hooks.announce('Enough diamonds. The exit is open.');
      } else if (e.type === 'tick' && (e.seconds === 10 || e.seconds === 5)) this.hooks.announce(`${e.seconds} seconds left`);
      else if (e.type === 'died') this.hooks.announce('Digby is lost.');
      else if (e.type === 'won') this.hooks.announce('Cave complete.');
      else if (e.type === 'timeout') this.hooks.announce('Out of time.');
    }
    if (s.score > this.lastScore) {
      const bonus = this.hooks.score(s.score - this.lastScore);
      this.lastScore = s.score;
      if (bonus > 0) this.celebrate();
    }
  }

  /** A bonus life: the space sparkles and a jingle plays. */
  celebrate(): void {
    if (!this.hooks.options().reduceFlashing) this.sparkleMs = 2200;
    this.hooks.audio.cue('bonusLife');
  }

  private countBonus(dt: number): void {
    if (this.bonusLeft <= 0) {
      if (this.phaseMs > 500) this.startOutro();
      return;
    }
    this.acc += dt;
    const per = Math.max(1, Math.ceil(this.bonusLeft / 150));
    while (this.acc >= BONUS_STEP_MS && this.bonusLeft > 0) {
      this.acc -= BONUS_STEP_MS;
      const n = Math.min(per, this.bonusLeft);
      this.bonusLeft -= n;
      this.hooks.audio.cue('bonusTick', this.bonusTicks++);
      if (this.hooks.score(n * this.state.params.level) > 0) this.celebrate();
      this.phaseMs = 0;
    }
  }

  private enter(phase: RunPhase): void {
    this.phase = phase;
    this.phaseMs = 0;
    this.acc = 0;
  }

  startOutro(): void {
    if (this.phase === 'outro' || this.phase === 'done') return;
    this.enter('outro');
    this.cover.start('cover', this.built.w, this.built.h, this.state.seed ^ 0x5bd1e995);
  }

  /** Fire / Enter: skip the wait after a death or OUT OF TIME. */
  confirm(): void {
    if ((this.phase === 'dead' || this.phase === 'timeout') && this.phaseMs > 400) this.startOutro();
  }

  /** How the cave ended, once `phase` is 'done'. */
  get outcome(): 'won' | 'died' | 'timeout' {
    const st = this.state.status;
    return st === 'won' ? 'won' : st === 'timeout' ? 'timeout' : 'died';
  }

  /** Time shown in the status line: counting down into the score at the exit. */
  get shownTime(): number {
    return this.phase === 'bonus' || (this.state.status === 'won' && this.phase !== 'play') ? this.bonusLeft : this.state.timeLeft;
  }
}
